import { useEffect, useState } from "react";
import { issueBoCode, listBoCodes, listBoVenues, revokeBoCode, type BoCode, type BoIssued, type BoVenue } from "../../api";
import { fill, shortDate, venueLabel, type BoProps } from "./Backoffice";

const NEW_VENUE = "__new__";

// The alphabet the sign-up screen keeps. Anything else it silently drops, so a
// code with an O in it would not be the code people type. The server refuses
// such a code too; checking here only says so before the round trip.
const CODE_RE = /^[A-HJ-NP-Z2-9]{6}$/;

export function Codes({
  t,
  locale,
  onError,
  onShowStaff,
  initialVenueId,
}: BoProps & { onShowStaff: (code: string) => void; initialVenueId: string | null }) {
  const [codes, setCodes] = useState<BoCode[]>([]);
  const [venues, setVenues] = useState<BoVenue[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = () =>
    Promise.all([listBoCodes(), listBoVenues()])
      .then(([c, v]) => {
        setCodes(c.codes);
        setVenues(v.venues);
        setLoadError(null);
      })
      .catch((e) => setLoadError(onError(e)));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bo">
      <IssueForm t={t} venues={venues} initialVenueId={initialVenueId} onError={onError} onIssued={load} />

      <h2>{t("bo.nav.codes")}</h2>
      {loadError && <p className="error">{loadError}</p>}
      {codes.length === 0 && !loadError ? (
        <p className="muted">{t("bo.codes.empty")}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t("bo.codes.code")}</th>
                <th>{t("bo.codes.venue")}</th>
                <th>{t("bo.codes.signups")}</th>
                <th>{t("bo.codes.status")}</th>
                <th>{t("bo.codes.issued")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {/* Newest first: the code just issued is the one being looked for. */}
              {[...codes].reverse().map((c) => (
                <CodeRow key={c.code} c={c} t={t} locale={locale} onError={onError} onShowStaff={onShowStaff} onChanged={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CodeRow({
  c,
  t,
  locale,
  onError,
  onShowStaff,
  onChanged,
}: Pick<BoProps, "t" | "locale" | "onError"> & { c: BoCode; onShowStaff: (code: string) => void; onChanged: () => void }) {
  // Two clicks, not a browser dialog: revoking is permanent, and a confirm()
  // box is the kind that gets dismissed by reflex.
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revoke() {
    setBusy(true);
    setError(null);
    try {
      await revokeBoCode(c.code);
      setConfirming(false);
      onChanged();
    } catch (e) {
      setError(onError(e));
    } finally {
      setBusy(false);
    }
  }

  const signups = c.max_uses == null ? String(c.uses) : `${c.uses} / ${c.max_uses}`;
  return (
    <>
      <tr className={c.status === "revoked" ? "dim" : ""}>
        <td className="mono strong">{c.code}</td>
        <td>{venueLabel(c.venue)}</td>
        <td>
          {c.uses > 0 ? (
            <button className="link" onClick={() => onShowStaff(c.code)} title={t("bo.codes.show_staff")}>
              {signups}
            </button>
          ) : (
            signups
          )}
        </td>
        <td>
          <span className={`badge ${c.status}`}>{t(`bo.status.${c.status}`)}</span>
          {c.personal && <span className="muted small"> · {t("bo.status.personal")}</span>}
        </td>
        <td className="muted">{shortDate(c.created_at, locale)}</td>
        <td className="row-actions">
          {c.status !== "revoked" && !confirming && (
            <button className="danger-link" onClick={() => setConfirming(true)}>
              {t("bo.codes.revoke")}
            </button>
          )}
        </td>
      </tr>
      {(confirming || error) && (
        <tr className="confirm-row">
          <td colSpan={6}>
            {confirming && (
              <div className="confirm">
                <span>{fill(t("bo.codes.revoke_warn"), { code: c.code })}</span>
                <button className="danger" onClick={revoke} disabled={busy}>
                  {t("bo.codes.revoke_confirm")}
                </button>
                <button className="link" onClick={() => setConfirming(false)} disabled={busy}>
                  {t("common.cancel")}
                </button>
              </div>
            )}
            {error && <p className="error">{error}</p>}
          </td>
        </tr>
      )}
    </>
  );
}

function IssueForm({
  t,
  venues,
  initialVenueId,
  onError,
  onIssued,
}: Pick<BoProps, "t" | "onError"> & { venues: BoVenue[]; initialVenueId: string | null; onIssued: () => void }) {
  const choosable = venues.filter((v) => !v.protected);
  const [venueId, setVenueId] = useState<string>(initialVenueId ?? "");
  const [name, setName] = useState("");
  const [hood, setHood] = useState("");
  const [code, setCode] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BoIssued | null>(null);

  // Until the list arrives nothing is selected; once it does, the first venue
  // is — or "new venue" when there are none yet.
  const selected = venueId || (choosable[0]?.id ?? NEW_VENUE);
  const isNew = selected === NEW_VENUE;
  const codeOk = code === "" || CODE_RE.test(code);
  const maxOk = maxUses === "" || /^\d+$/.test(maxUses);
  const canSubmit = !busy && codeOk && maxOk && (!isNew || name.trim() !== "");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await issueBoCode({
        ...(isNew ? { venue: name.trim(), neighborhood: hood.trim() } : { venue_id: selected }),
        code: code || undefined,
        max_uses: maxUses ? Number(maxUses) : undefined,
      });
      setResult(res);
      setCode("");
      setMaxUses("");
      if (isNew) {
        setName("");
        setHood("");
        setVenueId(res.venue.id);
      }
      onIssued();
    } catch (err) {
      setError(onError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={submit}>
      <h2>{t("bo.issue.title")}</h2>
      <div className="form-grid">
        <label>
          <span>{t("bo.issue.venue")}</span>
          <select value={selected} onChange={(e) => setVenueId(e.target.value)}>
            {choosable.map((v) => (
              <option key={v.id} value={v.id}>
                {venueLabel(v)}
              </option>
            ))}
            <option value={NEW_VENUE}>{t("bo.issue.new_venue")}</option>
          </select>
        </label>
        <label>
          <span>{t("bo.issue.code")}</span>
          <input
            className="mono"
            value={code}
            maxLength={6}
            placeholder={t("bo.issue.code_placeholder")}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
          />
        </label>
        {isNew && (
          <>
            <label>
              <span>{t("bo.issue.venue_name")}</span>
              <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              <span>{t("bo.issue.neighborhood")}</span>
              <input value={hood} maxLength={80} onChange={(e) => setHood(e.target.value)} />
            </label>
          </>
        )}
        <label>
          <span>{t("bo.issue.max_uses")}</span>
          <input inputMode="numeric" value={maxUses} placeholder={t("bo.issue.no_limit")} onChange={(e) => setMaxUses(e.target.value.trim())} />
        </label>
      </div>
      <p className={codeOk ? "muted small" : "error small"}>{t("bo.issue.code_hint")}</p>
      <button type="submit" disabled={!canSubmit}>
        {t("bo.issue.submit")}
      </button>
      {error && <p className="error">{error}</p>}
      {result && <IssueResult t={t} res={result} />}
    </form>
  );
}

function IssueResult({ t, res }: Pick<BoProps, "t"> & { res: BoIssued }) {
  const vars = { code: res.code, venue: venueLabel(res.venue), name: res.venue.name, hood: res.venue.neighborhood };
  return (
    <div className="result">
      {res.existing ? (
        <p>{fill(t("bo.issue.existing"), vars)}</p>
      ) : (
        <>
          <p className="big">
            <span className="mono strong">{res.code}</span> → {vars.venue}
            {res.venue_created && <span className="muted"> {t("bo.issue.venue_created")}</span>}
          </p>
          <p className="muted">{fill(t("bo.issue.tell"), vars)}</p>
        </>
      )}
      {res.neighborhood_ignored && <p className="muted small">{fill(t("bo.issue.hood_ignored"), vars)}</p>}
    </div>
  );
}
